import { forwardRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';

interface SaleTicketProps {
  sale: any;
}

export const SaleTicket = forwardRef<HTMLDivElement, SaleTicketProps>(({ sale }, ref) => {
  const { user } = useAuthStore();

  if (!sale) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return new Date().toLocaleString('es-MX');
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return new Date().toLocaleString('es-MX');
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      CASH: 'Efectivo',
      CARD: 'Tarjeta',
      TRANSFER: 'Transferencia',
      CREDIT: 'Crédito',
      MIXED: 'Mixto'
    };
    return labels[method] || method;
  };

  return (
    <div ref={ref} className="bg-white p-4 text-[12px] font-mono text-black w-[80mm] mx-auto leading-tight print-ticket">
      {/* Encabezado */}
      <div className="text-center mb-4">
        <h1 className="font-bold text-[16px] mb-1">{user?.company?.name || 'MI EMPRESA'}</h1>
        <p className="whitespace-pre-wrap">{user?.company?.ticketHeader || ''}</p>
        <p>Sucursal: {user?.branch?.name || 'Principal'}</p>
        <p>Fecha: {formatDate(sale.createdAt)}</p>
        {sale.status === 'QUOTATION' ? (
          <p className="font-bold mt-2 text-[14px]">Cotización: {sale.folio}</p>
        ) : sale.status === 'PENDING_PAYMENT' ? (
          <p className="font-bold mt-2 text-[14px]">Pendiente: {sale.folio}</p>
        ) : (
          <p className="font-bold mt-2 text-[14px]">Ticket: {sale.folio}</p>
        )}
        <p>Le atendió: {sale.user?.fullName || user?.fullName}</p>
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Artículos */}
      <table className="w-full mb-2">
        <thead>
          <tr className="border-b border-dashed border-black">
            <th className="text-left py-1 w-[15%]">CANT</th>
            <th className="text-left py-1">DESCRIPCIÓN</th>
            <th className="text-right py-1">IMPORTE</th>
          </tr>
        </thead>
        <tbody>
          {sale.items?.map((item: any) => (
            <tr key={item.id}>
              <td className="align-top py-1 text-center">{item.quantity}</td>
              <td className="align-top py-1 pr-1">
                {item.product?.name || 'Producto'}
                {Number(item.discount || 0) > 0 && (
                  <div className="text-[10px]">- Desc: ${(Number(item.discount) * item.quantity).toFixed(2)}</div>
                )}
              </td>
              <td className="align-top text-right py-1">${Number(item.total || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2"></div>

      {/* Totales */}
      <div className="flex justify-between mb-1">
        <span>SUBTOTAL:</span>
        <span>${Number(sale.subtotal || 0).toFixed(2)}</span>
      </div>
      {Number(sale.taxTotal || 0) > 0 && (
        <div className="flex justify-between mb-1 text-[11px] text-gray-700">
          <span>IVA / IMPUESTOS:</span>
          <span>${Number(sale.taxTotal).toFixed(2)}</span>
        </div>
      )}
      {Number(sale.discountTotal || 0) > 0 && (
        <div className="flex justify-between mb-1 text-red-600">
          <span>DESCUENTO:</span>
          <span>-${Number(sale.discountTotal).toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between mb-1 font-bold text-[16px]">
        <span>TOTAL:</span>
        <span>${Number(sale.grandTotal || 0).toFixed(2)}</span>
      </div>

      <div className="border-t border-dashed border-black my-2"></div>

      {sale.status !== 'QUOTATION' && (
        <>
          {/* Pagos */}
          {sale.payments?.map((payment: any, index: number) => {
            const isCash = payment.method === 'CASH';
            const displayAmount = isCash 
              ? Number(payment.amount || 0) + Number(sale.changeAmount || 0)
              : Number(payment.amount || 0);

            return (
              <div key={payment.id || index}>
                <div className="flex justify-between mb-1">
                  <span>SU PAGO ({getPaymentMethodLabel(payment.method)}):</span>
                  <span>${displayAmount.toFixed(2)}</span>
                </div>
                {!isCash && payment.reference && (
                  <div className="text-[10px] text-gray-600 mb-1">
                    Ref: {payment.reference}
                  </div>
                )}
              </div>
            );
          })}
          {Number(sale.changeAmount || 0) > 0 && (
            <div className="flex justify-between mb-1 font-bold">
              <span>SU CAMBIO:</span>
              <span>${Number(sale.changeAmount).toFixed(2)}</span>
            </div>
          )}
        </>
      )}

      {sale.notes && (
        <>
          <div className="border-t border-dashed border-black my-2"></div>
          <p className="text-center font-bold">NOTAS:</p>
          <p className="text-center">{sale.notes}</p>
        </>
      )}

      {sale.status === 'QUOTATION' ? (
        <div className="text-center mt-6">
          <p className="font-bold tracking-widest text-[14px]">*** COTIZACIÓN ***</p>
          <p className="mt-1">DOCUMENTO NO VÁLIDO COMO RECIBO DE PAGO</p>
          <p className="whitespace-pre-wrap mt-2">{user?.company?.ticketFooter || ''}</p>
        </div>
      ) : sale.status === 'PENDING_PAYMENT' ? (
        <div className="text-center mt-6">
          <p className="font-bold tracking-widest text-[14px]">*** PENDIENTE DE PAGO ***</p>
          <p className="mt-1">DOCUMENTO NO VÁLIDO COMO RECIBO DE PAGO</p>
          <p className="whitespace-pre-wrap mt-2">{user?.company?.ticketFooter || ''}</p>
        </div>
      ) : (
        <div className="text-center mt-6">
          <p>¡Gracias por su compra!</p>
          <p className="whitespace-pre-wrap mt-2">{user?.company?.ticketFooter || ''}</p>
        </div>
      )}

      <style>{`
        @media print {
          @page { margin: 0; }
          body { margin: 0; }
          .print-ticket { width: 80mm !important; margin: 0 auto !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
});

SaleTicket.displayName = 'SaleTicket';
